import React, { useMemo } from 'react';
import { Button, Modal, ModalInner, Text } from 'components';
import { ScheduleGridData } from './types';

type Currency = 'KRW' | 'USD';

// 정보 아이콘 SVG
const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

interface ConfirmModalProps {
  data: ScheduleGridData[] | null;
  amount: { currency: Currency | string; value: number };
  country: string | null;
  onClose: () => void;
  onStartToss: () => void;
  onStartAccountTransfer: () => void;
  freePolicy: boolean; // freePolicy prop 추가
}

const SYMBOL: Record<Currency, string> = { KRW: '₩', USD: '$' };

function formatMoney(value: number, currency: Currency): string {
  const safe = Number.isFinite(value) ? value : 0;
  if (currency === 'KRW') {
    return safe.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ConfirmModal = ({
  data,
  amount,
  onClose,
  onStartToss,
  onStartAccountTransfer,
  freePolicy, // freePolicy prop 받기
}: ConfirmModalProps) => {
  const currency = (amount?.currency === 'KRW' ? 'KRW' : 'USD') as Currency;

  const totalLabel = useMemo(() => {
    const symbol = SYMBOL[currency];
    return `${symbol}${formatMoney(amount?.value ?? 0, currency)}`;
  }, [amount?.value, currency]);

  const isOpen = !!(data && data.length > 0);

  return (
    <Modal isOpened={isOpen}>
      <ModalInner className="md:w-[400px]" title="Choose a Payment Method" onClickClose={onClose}>
        <div className="flex flex-col items-center justify-center p-4 pt-2">
          <Text color="tertiary" weight="regular">
            Total to Pay
          </Text>
          <Text weight="semibold" size="heading1" className="mt-1" aria-label="total-to-pay">
            {totalLabel}
          </Text>
        </div>

        {/* 무료 정책 안내 문구 - 금액 아래, 버튼 위에 위치 */}
        {freePolicy && (
          <div className="px-4 pb-3 text-center">
            <div className="inline-flex items-center gap-1.5 text-sm text-red-500">
              <InfoIcon />
              <span>무료 정책이 적용되어 Toss 결제는 불가능합니다.</span>
            </div>
          </div>
        )}

        <div className="flex w-full flex-row items-center justify-center gap-3 bg-bg-transparent_gray_light p-4">
          <Button
            className={`h-11 flex-1 flex items-center justify-center ${freePolicy ? 'opacity-50 cursor-not-allowed' : ''}`}
            text="Toss"
            onClick={freePolicy ? undefined : onStartToss}
            type="blue"
            size="large"
            aria-label="pay-with-toss"
            disabled={freePolicy}
          />
          <Button
            className="h-11 flex-1 flex items-center justify-center"
            text="Bank"
            onClick={onStartAccountTransfer}
            type="primary"
            size="large"
            aria-label="pay-with-bank-transfer"
          />
          <Button
            className="h-11 flex-1 flex items-center justify-center"
            text="Cancel"
            onClick={onClose}
            type="secondary"
            size="large"
            aria-label="cancel-payment"
          />
        </div>
      </ModalInner>
    </Modal>
  );
};

export default ConfirmModal;